import { LeftOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import styled from 'styled-components';

import CancelIcon from '../../../components/UI/Icon-SVG/CancelIcon';
import FileIcon from '../../../components/UI/Icon-SVG/FileIcon';
import MicrophoneIcon from '../../../components/UI/Icon-SVG/MicrophoneIcon';
import { useRecorder } from '../hooks/useRecord';
import useSendMessage from '../hooks/useSendMessage';

const Container = styled.div`
  height: 84px;
  padding: 16px;
  border-top: 1px solid #d7def3;
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: flex-start;
`;

const FieldWrapper = styled.div`
  background: white;
  border: 1px solid #d7def3;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  border-radius: 8px;
  height: 52px;
  padding: 10px;
  max-width: 687px;
  width: 100%;
  margin: 0 auto;
  position: relative;
  z-index: 3;
`;

const File = styled.label`
  padding: 2px 10px 2px 2px;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  border-right: 1px solid #d7def3;
  position: relative;
`;

const InputFile = styled.input`
  display: none;
`;

const Microphone = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  transition: 0.4s all;
  border-radius: 50px;
  position: absolute;
`;

const InputWrapper = styled.div`
  display: flex;
  flex: 1;
  padding-right: 50px;
  position: relative;
`;

const TextArea = styled(TextareaAutosize)`
  display: flex;
  flex: 1;
  border: 0;
  outline: none;
  padding: 0px 16px;
  font-size: 16px;
  font-family: 'Open Sans', sans-serif;
  user-select: none;
  height: 36px;
  position: absolute;
  width: calc(100% - 35px);
  z-index: 4;
  resize: none;

  &::placeholder {
    color: #a6b0cf;
  }
`;

const RecordInfoWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 0px 20px;
  align-items: center;
  flex: 1;
`;

const RecordTimer = styled.p`
  font-size: 16px;
  font-family: 'Open Sans', sans-serif;
  user-select: none;
  margin: 0;
`;

const CancelText = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 16px;
  font-family: 'Open Sans', sans-serif;
  user-select: none;
  margin: 0;
  color: #a6b0cf;
  padding-right: 20px;
`;

const ReplyMessage = styled.div`
  position: absolute;
  width: 100%;
  height: 52px;
  background: white;
  border-radius: 8px;
  top: -52px;
  z-index: 3;
  left: 0;
  border: 1px solid #d7def3;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
`;

const ReplyMessageText = styled.div`
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  border-left: 2px solid #397cf3;
  padding-left: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ReplyMessageCancel = styled.div`
  cursor: pointer;
`;

const MessageInput = ({
  activeChat,
  userId,
  isSupportChat = false,
  setReplyMessage,
  replyMessage,
}) => {
  const [textValue, setTextValue] = useState('');
  const [textAreaHeight, setTextAreaHeight] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingStatus, setRecordingStatus] = useState('paused');
  const [mouseXPosition, setMouseXPosition] = useState(0);
  const [mousedownFirstPosition, setMousedownFirstPosition] = useState(0);
  const [, setRecordTimer] = useState(0);
  const [recordTimerTime, setRecordTimerTime] = useState('00:00');
  const [startTime, setStartTime] = useState(0);

  const { sendMessage, sendFile, sendAudio, sendingAudio } = useSendMessage({
    activeChat,
    userId,
    replyMessage,
    setReplyMessage,
  });

  const handleChange = (e) => {
    if (e.target.value !== '\n') {
      setTextValue(e.target.value);
    }
  };
  const cancelReplyMessage = () => setReplyMessage(null);

  const onReadyAudioFile = useCallback(
    (file) => {
      // min duration for audio 1000ms
      if (recordingStatus === 'ready' && file && new Date().getTime() - startTime > 1000) {
        sendAudio(file);
        setRecordingStatus('paused');
      }
    },
    [recordingStatus],
  );

  const { startRecording, stopRecording } = useRecorder(onReadyAudioFile);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    file && sendFile(file);
  };

  const onInputClick = (event) => {
    event.target.value = '';
  };

  const handleKeyUp = (e) => {
    if (e.key === 'Enter') {
      textValue && sendMessage(textValue);
      setTextValue('');
    }
  };

  useEffect(() => setTextValue(''), [activeChat]);

  const handleMouseMove = (event) => {
    if (isRecording) {
      setMouseXPosition(event.clientX);
    }
  };

  const handleMouseDown = (event) => {
    if (!activeChat) return;
    setStartTime(new Date().getTime());
    setIsRecording(true);
    setMousedownFirstPosition(event.clientX);
    startRecording();
    setRecordingStatus('start');
  };

  const handleMouseUp = () => {
    setIsRecording(false);
    setMouseXPosition(0);
    setRecordingStatus('ready');
    stopRecording();
  };

  const cancelRecording = () => {
    if (isRecording) {
      stopRecording();
      setIsRecording(false);
      setMouseXPosition(0);
      setRecordingStatus('paused');
    }
  };

  useEffect(() => {
    let id;
    if (recordingStatus === 'start') {
      id = setInterval(() => {
        setRecordTimer((duration) => {
          setRecordTimerTime(moment.utc((duration + 1) * 1000).format('mm:ss'));
          return duration + 1;
        });
      }, 1000);
    } else {
      setRecordTimer(0);
      setRecordTimerTime('00:00');
    }
    return () => {
      id && clearInterval(id);
    };
  }, [recordingStatus]);

  useEffect(() => {
    document.body.addEventListener('mouseup', handleMouseUp);
    document.body.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.body.removeEventListener('mouseup', handleMouseUp);
      document.body.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isRecording]);

  const handleHeightChange = (height) => {
    setTextAreaHeight(height);
  };

  const microphoneCancelPosition =
    !mousedownFirstPosition || !mouseXPosition || mouseXPosition - mousedownFirstPosition > -40
      ? -4
      : -4 - (mouseXPosition - mousedownFirstPosition);

  if (mousedownFirstPosition && mouseXPosition && mousedownFirstPosition - mouseXPosition > 220) {
    cancelRecording();
  }

  return (
    <Container
      style={{
        background: isSupportChat ? '#f8f8fb' : '#EDEDF5',
        border: isSupportChat ? 'none' : '1px solid #D7DEF3',
      }}
    >
      <FieldWrapper
        style={{
          height: `${textAreaHeight + 27}px`,
        }}
      >
        {!!replyMessage && (
          <ReplyMessage>
            <ReplyMessageText>{replyMessage.text}</ReplyMessageText>
            <ReplyMessageCancel onClick={cancelReplyMessage}>
              <CancelIcon color="#d7def3" />
            </ReplyMessageCancel>
          </ReplyMessage>
        )}
        <File htmlFor="upload">
          <InputFile
            type="file"
            id="upload"
            onChange={handleFileChange}
            onClick={onInputClick}
            disabled={!activeChat}
          />
          <FileIcon />
        </File>
        <InputWrapper>
          {isRecording ? (
            <RecordInfoWrapper>
              <RecordTimer>{recordTimerTime}</RecordTimer>
              <CancelText>
                <LeftOutlined />
                &nbsp;&nbsp;&nbsp; Slide to cancel
              </CancelText>
            </RecordInfoWrapper>
          ) : (
            <TextArea
              onHeightChange={handleHeightChange}
              disabled={!activeChat}
              placeholder="Message"
              value={textValue}
              maxRows={10}
              onChange={handleChange}
              onKeyUp={handleKeyUp}
              style={{
                bottom: `-${textAreaHeight / 2}px`,
              }}
            />
          )}
        </InputWrapper>
        <Microphone
          style={{
            width: isRecording ? 70 : 32,
            height: isRecording ? 70 : 32,
            right: isRecording ? microphoneCancelPosition : 15,
            background: isRecording ? '#397EF3' : 'transparent',
          }}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
        >
          {!sendingAudio ? (
            <MicrophoneIcon color={isRecording ? '#FFFFFF' : '#A6B0CF'} />
          ) : (
            <Spin />
          )}
        </Microphone>
      </FieldWrapper>
    </Container>
  );
};

export default MessageInput;
